import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { buildClientManagementPath, type NotificationTarget } from 'components/notifications/services/actionHandler';
import useCurrentUser from 'hooks/useCurrentUser';
import useSwitchProject from 'hooks/useSwitchProject';
import rollbar from 'rollbarClient';

const NOTIFICATION_CACHE = 'notifications-v1';
const PENDING_NOTIFICATION_KEY = '/__pending-notification';
const PENDING_NOTIFICATION_TTL_MS = 5 * 60 * 1000;

type PushPayload = Record<string, string>;

interface PendingNotification {
  payload: PushPayload;
  storedAt: number;
  switchAttempted?: boolean;
}

interface PushRecipient {
  userId: number;
  projectId: number | null;
}

function toPositiveInteger(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/** The Identity account the push was sent to; `recipientProjectId` is empty for the Lidz admin account. */
function parsePushRecipient(payload: PushPayload): PushRecipient | null {
  const userId = toPositiveInteger(payload.recipientUserId);
  if (userId === null) return null;
  if (payload.recipientProjectId === '') return { userId, projectId: null };

  const projectId = toPositiveInteger(payload.recipientProjectId);
  return projectId === null ? null : { userId, projectId };
}

async function storePendingNotification(pending: PendingNotification): Promise<void> {
  const cache = await caches.open(NOTIFICATION_CACHE);
  await cache.put(PENDING_NOTIFICATION_KEY, new Response(JSON.stringify(pending)));
}

async function clearPendingNotification(): Promise<void> {
  const cache = await caches.open(NOTIFICATION_CACHE);
  await cache.delete(PENDING_NOTIFICATION_KEY);
}

function parsePushTarget(payload: PushPayload): NotificationTarget | null {
  const clientId = toPositiveInteger(payload.clientId);
  const sourceId = toPositiveInteger(payload.sourceId);

  if (clientId === null || sourceId === null || !payload.sourceType) return null;

  return {
    clientId,
    sourceId,
    sourceType: payload.sourceType,
    projectId: toPositiveInteger(payload.projectId),
    messageId: toPositiveInteger(payload.messageId),
  };
}

export default function usePushNotificationNavigation(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const { switchToProject } = useSwitchProject();

  const navigateToNotificationRef = useRef<(payload: PushPayload, canSwitchAccount?: boolean) => boolean>(() => false);
  // signInUser swaps currentUser before the reload; the page being discarded must not consume the pending push.
  const isSwitchingAccountRef = useRef(false);

  useEffect(() => {
    // The pending notification survives the reload and is opened once the session belongs to its recipient.
    const switchAccountAndResume = async (payload: PushPayload, projectId: number | null): Promise<void> => {
      isSwitchingAccountRef.current = true;
      await storePendingNotification({ payload, storedAt: Date.now(), switchAttempted: true }).catch(() => undefined);
      try {
        await switchToProject(projectId);
      } catch (error) {
        isSwitchingAccountRef.current = false;
        await clearPendingNotification().catch(() => undefined);
        rollbar.warning('Push notification account switch failed', error as Error);
      }
    };

    navigateToNotificationRef.current = (payload: PushPayload, canSwitchAccount = true): boolean => {
      if (!currentUser) return false;

      const target = parsePushTarget(payload);
      if (!target) return false;

      const recipient = parsePushRecipient(payload);
      if (recipient && recipient.userId !== currentUser.id) {
        if (!canSwitchAccount) return false;
        void switchAccountAndResume(payload, recipient.projectId);
        return true;
      }

      navigate(
        buildClientManagementPath(target, {
          currentPathname: location.pathname,
          currentSearch: location.search,
          focusAlertMessage: true,
        })
      );
      return true;
    };
  }, [currentUser, location.pathname, location.search, navigate, switchToProject]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const handleMessage = (event: MessageEvent): void => {
      if (event.data?.type !== 'NOTIFICATION_CLICK') return;

      if (navigateToNotificationRef.current(event.data.payload ?? {})) {
        (event.source as ServiceWorker | null)?.postMessage({
          type: 'NOTIFICATION_CLICK_HANDLED',
          clickId: event.data.clickId,
        });
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!currentUser || !('caches' in window) || isSwitchingAccountRef.current) return undefined;

    let cancelled = false;

    const consumePendingNotification = async (): Promise<void> => {
      const cache = await caches.open(NOTIFICATION_CACHE);
      const response = await cache.match(PENDING_NOTIFICATION_KEY);
      if (!response || cancelled || isSwitchingAccountRef.current) return;

      await cache.delete(PENDING_NOTIFICATION_KEY);

      const pending = (await response.json()) as PendingNotification;
      if (Date.now() - pending.storedAt > PENDING_NOTIFICATION_TTL_MS) return;

      navigateToNotificationRef.current(pending.payload, !pending.switchAttempted);
    };

    void consumePendingNotification().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [currentUser]);
}
